(() => {
  const selector = 'slider-component, slideshow-component';
  const instances = new Map();

  function initialise(component) {
    if (instances.has(component)) return;

    const slider = component.querySelector('[id^="Slider-"]');
    const controls = component.querySelector('.slider-buttons');

    if (!slider || !controls) return;

    // Only use controls belonging to this carousel.
    if (controls.closest(selector) !== component) return;

    const dots = document.createElement('div');
    dots.className = 'luminessa-dots';
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', 'Carousel navigation');

    controls.appendChild(dots);

    let frame;
    let previousCount = 0;

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    function update() {
      const count = Number(component.totalPages);
      const current = Number(component.currentPage);

      // Keep original controls if Dawn has not initialised.
      if (!Number.isFinite(count) || !Number.isFinite(current)) {
        controls.classList.remove('luminessa-dots-ready');
        dots.hidden = true;
        return;
      }

      dots.hidden = count <= 1;

      if (count !== previousCount) {
        dots.replaceChildren();

        for (let index = 0; index < count; index++) {
          const button = document.createElement('button');

          button.type = 'button';
          button.className = 'luminessa-dot';
          button.setAttribute(
            'aria-label',
            `Go to carousel position ${index + 1} of ${count}`
          );
          button.setAttribute('aria-controls', slider.id);

          button.addEventListener('click', () => {
            // Use Dawn's own slideshow navigation when available.
            const nativeLink = component.isConnected &&
              component.querySelectorAll(
                '.slideshow__control-wrapper .slider-counter__link'
              )[index];

            if (nativeLink) {
              nativeLink.click();
              return;
            }

            const offset = Number(component.sliderItemOffset);
            const page = Number(component.currentPage);

            if (!offset || !Number.isFinite(page)) return;

            slider.scrollTo({
              left: slider.scrollLeft + (index + 1 - page) * offset,
              behavior: matchMedia(
                '(prefers-reduced-motion: reduce)'
              ).matches ? 'instant' : 'smooth'
            });
          });

          dots.appendChild(button);
        }

        previousCount = count;
      }

      Array.from(dots.children).forEach((button, index) => {
        const distance = Math.abs(index + 1 - current);

        button.setAttribute(
          'aria-current',
          distance === 0 ? 'true' : 'false'
        );

        button.style.setProperty(
          '--dot-opacity',
          distance === 0 ? '1' : distance === 1 ? '0.5' : '0.25'
        );
      });

      controls.classList.add('luminessa-dots-ready');
    }

    const observer = new ResizeObserver(schedule);
    observer.observe(slider);

    slider.addEventListener('scroll', schedule, { passive: true });
    component.addEventListener('slideChanged', schedule);
    window.addEventListener('resize', schedule);

    instances.set(component, () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      slider.removeEventListener('scroll', schedule);
      component.removeEventListener('slideChanged', schedule);
      window.removeEventListener('resize', schedule);
      controls.classList.remove('luminessa-dots-ready');
      dots.remove();
    });

    schedule();
  }

  function scan(root = document) {
    if (root.matches?.(selector)) initialise(root);
    root.querySelectorAll(selector).forEach(initialise);
  }

  async function start() {
    await customElements.whenDefined('slider-component');
    scan();

    document.addEventListener('shopify:section:load', (event) => {
      scan(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      instances.forEach((cleanup, component) => {
        if (event.target.contains(component)) {
          cleanup();
          instances.delete(component);
        }
      });
    });

    window.addEventListener('load', () => {
      window.dispatchEvent(new Event('resize'));
    }, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();